import { Box, ThemeProvider } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import axios from 'axios';
import { useAuth } from 'contexts/auth-context';
import { STALE_TIME } from 'lib/constants/query';
import { enqueueSnackbar } from 'notistack';
import { lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
/* <Errors, Wrappers, and Conditional Routes >*/
import ErrorRouteElement from 'pages/errors/error-route-element';
import NoAccessIndex from 'pages/errors/no-access-index';
import NotFoundIndex from 'pages/errors/not-found-index';
import AccessRoleRoutes from 'pages/wrappers/access-role-routes';
import ConditionalRoutes from 'pages/wrappers/conditional-routes';
import LazyRoute from 'pages/wrappers/lazy-route';
import ProtectedRoute from 'pages/wrappers/protected-route';
import RootUserRoute from 'pages/wrappers/root-user-route';
/* <Errors, Wrappers, and Conditional Routes />*/
/* <Preloaded Pages> */
import SignIn from 'pages/sign-in';
/* </Preloaded Pages> */
import Header from 'components/header/header';
import { lightTheme } from 'themes/default.light';
import './App.css';

/* <Home> */
const Home = lazy(() => import('pages/home'));
/* </Home> */

/* <Admin> */
const AdminIndex = lazy(() => import('pages/admin-index'));
const AdminProfileIndex = lazy(() => import('components/admin/admin-profile/admin-profile-index'));
const AdminUserIndex = lazy(() => import('components/admin/admin-users/admin-user-index'));
const AdminSettingsIndex = lazy(() =>
  import('components/admin/admin-settings/admin-settings-index')
);
const AdminRegulationViewerIndex = lazy(() =>
  import('components/admin/regulation-viewer/regulation-viewer-index')
);
const AdminActorsIndex = lazy(() => import('components/admin/admin-actors/admin-actors-index'));
/* </Admin> */

/* <Compliance> */
const ComplianceIndex = lazy(() => import('pages/compliance-index'));
const ComplianceAIProducts = lazy(() =>
  import('components/compliance/product/compliance-ai-products')
);
const ComplianceActivities = lazy(() =>
  import('components/compliance/actions/compliance-activities')
);
const ComplianceCustomPoliciesIndex = lazy(() =>
  import('components/compliance/custom-policies/compliance-custom-policies-index')
);
// const ComplianceCustomPolicy = lazy(() =>
//   import('components/compliance/custom-policies/compliance-custom-policy')
// );
const ComplianceCanada = lazy(() =>
  import('components/compliance/dynamic-policies/compliance-canada')
);
const ComplianceEuAct = lazy(() => import('components/compliance/dynamic-policies/compliance-eu'));
const ComplianceUK = lazy(() => import('components/compliance/dynamic-policies/compliance-uk'));
const ComplianceRMF = lazy(() => import('components/compliance/rmf/compliance-rmf'));
const ComplilanceUsersIndex = lazy(() =>
  import('components/compliance/users/compliance-users-index')
);
const ComplianceRiskIndex = lazy(() => import('components/compliance/risk/compliance-risk-index'));

/* <Product Stepper> */
const ProductStepperIndex = lazy(() =>
  import('components/compliance/product/product-stepper/product-stepper-index')
);
const NewProductIndex = lazy(() =>
  import('components/compliance/product/product-stepper/new-product/new-product-index')
);
const AssignProductActivitiesIndex = lazy(() =>
  import(
    'components/compliance/product/product-stepper/assign-product-actions/assign-product-activities-index'
  )
);
const ReviewRisksIndex = lazy(() =>
  import('components/compliance/product/product-stepper/review-risks/review-risks-index')
);
const AssignRiskActivitiesIndex = lazy(() =>
  import(
    'components/compliance/product/product-stepper/assign-actions/assign-risk-activities-index'
  )
);
/* </Product Stepper> */

/* </Compliance> */

/* <Compliance Dashboard> */
const DashboardIndex = lazy(() => import('components/compliance/dashboard/dashboard-index'));
const DashboardActionTotal = lazy(() =>
  import('components/compliance/dashboard/dashboard-action/dashboard-action-total')
);
const DashboardProductsType = lazy(() =>
  import('components/compliance/dashboard/dashboard-product/dashboard-products-index')
);
const DashboardStatusIndex = lazy(() =>
  import('components/compliance/dashboard/dashboard-status/dashboard-status-index')
);
const DashboardUsersIndex = lazy(() =>
  import('components/compliance/dashboard/dashboard-user/dashboard-user-index')
);
const DashboardRisksIndex = lazy(() =>
  import('components/compliance/dashboard/dashboard-risk/dashboard-risks-index')
);
const DashboardPostureIndex = lazy(() =>
  import('components/compliance/dashboard/dashboard-posture/dashboard-posture-index.jsx')
);
const DashboardMapIndex = lazy(() =>
  import('components/compliance/dashboard/dashboard-map/dashboard-map-index')
);
/* </Compliance Dashboard> */

/* <Risk Review > */
const RiskReviewIndex = lazy(() => import('pages/risk-review-index'));
const RiskReviewHostIndex = lazy(() =>
  import('components/risk-review/host/risk-review-host-index')
);
const RiskReviewLandingIndex = lazy(() =>
  import('components/risk-review/landing/risk-review-landing-index')
);
const RiskReviewerIndex = lazy(() => import('components/risk-review/reviewer/risk-reviewer-index'));
/* </Risk Review> */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME
    }
  }
});

function App() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signOut, refreshToken } = useAuth();

  useEffect(() => {
    if (user && user.token) {
      refreshToken().catch((err) => {
        console.error('refresh error:', String(err));
        // unique case with an unprotected route but requires host to be signed in
        if (location.pathname.indexOf('/risk-review') > -1) {
          return signOut();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.status === 401 || error?.response?.status === 401) {
        if (location.pathname !== '/signin') {
          // TODO figure out how this works with react-query
          return signOut();
        }
      }

      error = error.response ? error.response : error;
      const statusText = error.name ?? error.data?.name ?? error.statusText ?? error.status ?? '';
      enqueueSnackbar(
        `${statusText ? statusText + ':' : ''} ${
          error.data?.message ?? error.message ?? 'Unknown Error'
        }`,
        { variant: 'error' }
      );

      return Promise.reject(error);
    }
  );

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={lightTheme}>
          <Box
            className="App"
            sx={{ display: 'flex', flexDirection: 'column', width: '100%', minHeight: '100svh' }}
          >
            <Header />
            <Routes>
              <Route
                index
                element={
                  <RootUserRoute>
                    <Home />
                  </RootUserRoute>
                }
                errorElement={<ErrorRouteElement />}
              />

              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminIndex />
                  </ProtectedRoute>
                }
              >
                <Route path="profile" element={<AdminProfileIndex />} />
                <Route
                  element={
                    <ConditionalRoutes
                      isAllowed={user?.isAdmin || user?.isRoot}
                      redirectTo={{
                        ...location,
                        pathname: `/admin/profile`
                      }}
                    />
                  }
                >
                  <Route path="users" element={<AdminUserIndex />} />
                  <Route path="settings" element={<AdminSettingsIndex />} />
                  <Route path="regulation-viewer" element={<AdminRegulationViewerIndex />} />
                  <Route path="actor-editor" element={<AdminActorsIndex />} />
                </Route>
                <Route
                  element={
                    <ConditionalRoutes
                      isAllowed={user?.isRoot}
                      redirectTo={{
                        ...location,
                        pathname: `/admin/profile`
                      }}
                    />
                  }
                >
                  <Route path="regulation-editor" element={<AdminRegulationViewerIndex />} />
                </Route>
              </Route>

              <Route
                path="/compliance/:orgId"
                element={
                  <ProtectedRoute>
                    <AccessRoleRoutes user={user} redirectTo={{ pathname: '/no-access' }} />
                  </ProtectedRoute>
                }
                errorElement={<ErrorRouteElement />}
              >
                <Route element={<ComplianceIndex />}>
                  <Route path="dashboard" element={<DashboardIndex />}>
                    {/* Nested products route under dashboard */}
                    <Route path="status-summary" element={<DashboardStatusIndex />} />
                    <Route path="product-types" element={<DashboardProductsType />} />
                    <Route path="top-activities" element={<DashboardActionTotal />} />
                    <Route path="top-users" element={<DashboardUsersIndex />} />
                    <Route path="risks" element={<DashboardRisksIndex />} />
                    <Route path="nist" element={<ComplianceRMF />} />
                    <Route path="posture" element={<DashboardPostureIndex />} />
                    <Route path="map" element={<DashboardMapIndex />} />

                    <Route
                      index
                      element={
                        <Navigate to={{ ...location, pathname: 'status-summary' }} replace />
                      }
                    />
                  </Route>
                  <Route path="users" element={<ComplilanceUsersIndex />} />
                  <Route path="nist" element={<ComplianceRMF />} />

                  <Route path="product-stepper" element={<ProductStepperIndex />}>
                    <Route
                      index
                      element={<Navigate to={{ ...location, pathname: 'new-product' }} replace />}
                    />
                    <Route path="new-product" element={<NewProductIndex />} />
                    <Route path=":productId/edit-product" element={<NewProductIndex />} />
                    <Route
                      path=":productId/assign-product-actions"
                      element={<AssignProductActivitiesIndex />}
                    />
                    <Route path=":productId/review-risks" element={<ReviewRisksIndex />} />
                    <Route
                      path=":productId/assign-risk-actions"
                      element={<AssignRiskActivitiesIndex />}
                    />
                  </Route>

                  <Route
                    element={
                      /* TODO: this is where products / EDIT PRODUCT is handled, fix path here */
                      <ConditionalRoutes
                        isAllowed={!searchParams.has('prId')}
                        redirectTo={{
                          ...location,
                          pathname: `product-stepper/${searchParams.get('prId')}/edit-product`
                        }}
                      />
                    }
                  >
                    <Route path="products" element={<ComplianceAIProducts />} />
                  </Route>
                  <Route path="actions" element={<ComplianceActivities />} />
                  <Route path="documents" element={<ComplianceActivities />} />
                  {/* <Route
                    element={
                      <ConditionalRoutes
                        isAllowed={searchParams.has('prId')}
                        redirectTo={{
                          ...location,
                          pathname: `product/${searchParams.get('prId')}`
                        }}
                      />
                    }
                  > */}
                  <Route path="risks" element={<ComplianceRiskIndex />} />

                  {/* Dynamic Compliance Routes */}
                  <Route path="canada" element={<ComplianceCanada />} />
                  <Route path="eu" element={<ComplianceEuAct />} />
                  <Route path="uk" element={<ComplianceUK />} />
                  <Route path="custom-policies" element={<ComplianceCustomPoliciesIndex />} />
                  {/* <Route
                    path="custom-policy/:policyType/:policyId?"
                    element={<ComplianceCustomPolicy />}
                  /> */}
                  {/* Dynamic Compliance Routes END */}
                  <Route
                    index
                    element={<Navigate to={{ ...location, pathname: 'dashboard' }} replace />}
                  />
                </Route>
              </Route>
              <Route
                path="/risk-review/:sessionId"
                element={
                  <LazyRoute>
                    <RiskReviewIndex />
                  </LazyRoute>
                }
                errorElement={<ErrorRouteElement />}
              >
                <Route path="landing" element={<RiskReviewLandingIndex />} />
                <Route path="host" element={<RiskReviewHostIndex />} />
                <Route path="reviewer" element={<RiskReviewerIndex />} />
                <Route
                  index
                  element={<Navigate to={{ ...location, pathname: 'landing' }} replace />}
                />
              </Route>
              <Route path="/signin" element={<SignIn />} errorElement={<ErrorRouteElement />} />
              <Route path="/no-access" element={<NoAccessIndex />} />
              <Route path="*" element={<NotFoundIndex />} />
              {/* Add more routes as needed */}
            </Routes>
          </Box>
        </ThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
